import React from 'react';

export const VARIANT = 'block';
export const FORGE_AI_ICON = 'forge_ai_icon';
export const BODY_SLOT = 'body';

export const PROMPT_TYPES = {
  FIND_SAVINGS: 'find_savings',
  PROGRAM_DOSSIER: 'program_dossier',
  CHAT: 'chat',
  QUICK_WINS: 'quick_wins',
  REIMAGINE_WITH_AI: 'reimagine_with_ai'
};

export const SUGGESTIONS = [
  { text: 'What are the skills and capabilities of this tool? How can you help me apply PBB?', value: 'What are the skills and capabilities of this tool? How can you help me apply PBB?', showInit: true },
  { text: 'What departments are available in my data?', value: 'What departments are available in my data?', showInit: true },
  { text: 'What are the top 25 high-cost programs?', value: 'What are the top 25 high-cost programs?', showInit: true },
  { text: 'Which programs are most efficient compared to peers?', value: 'Which programs are most efficient compared to peers?', showInit: true },
  { text: 'What process overlaps exist across departments?', value: 'What process overlaps exist across departments?', showInit: false },
  { text: 'What revenue enhancement opportunities exist?', value: 'What revenue enhancement opportunities exist?', showInit: false }
];

export const generateMessage = (message, type, data) => {
  switch (type) {
    case PROMPT_TYPES.PROGRAM_DOSSIER: return getDossierMessage(data);
    case PROMPT_TYPES.FIND_SAVINGS: return getSavingsMessage(data);
    case PROMPT_TYPES.QUICK_WINS: return 'Please find my quick wins based on the selected criteria.';
    case PROMPT_TYPES.REIMAGINE_WITH_AI: return 'Please reimagine with AI based on the selected criteria.';
    default: return message;
  }
};

const getDossierMessage = (data) => {
  if (data?.departmentName) return `Show me a summary of the ${data?.programName} program and highlight what’s relevant for the ${data?.departmentName} department.`;
  return `Show me a summary of the ${data?.programName} program, including its objectives, eligibility criteria, and key requirements.`;
};

const getSavingsMessage = (data) => {
  if (data.departmentName) return `Help me identify savings opportunities in the ${data?.departmentName} department to reduce costs by around ${data?.amount}`;
  return `Help me identify potential savings opportunities to reduce costs by approximately ${data?.amount}.`;
};

export const getPBBStrategistHeaderButtons = (toggleSavings, toggleDossierDialog, toggleQuickWinsDialog, toggleImagineWithAI, disableButtons) => {
  return [
    {
      id: 'more',
      name: 'More',
      variant: 'text',
      type: 'dropDown',
      popOverIcon: false,
      icon: 'dots_vertical',
      children: getMoreMenuOptions(disableButtons),
      onClickFunction: (e) => {
        switch (e.detail.value) {
          case 'program_dossier':
            toggleDossierDialog();
            break;
          case 'find_quick_wins':
            toggleQuickWinsDialog();
            break;
          case 'imagine_with_ai':
            toggleImagineWithAI();
            break;
          default:
            break;
        }
      },
    },
    {
      id: 'find_savings',
      name: 'Find savings',
      variant: 'text',
      icon: 'piggy_bank',
      onClickFunction: () => toggleSavings(),
      disabled: disableButtons,
    }
  ];
};

const getMoreMenuOptions = (disableButtons) => {
  return [
    {
      label: 'Program dossier',
      value: 'program_dossier',
      icon: 'folder_text',
      leadingIconType: 'component',
      leadingIconComponentProps: { external: true, externalType: 'all' },
      disabled: disableButtons,
    },
    {
      label: 'Find quick wins',
      value: 'find_quick_wins',
      icon: 'trophy_outline',
      leadingIconType: 'component',
      leadingIconComponentProps: { external: true, externalType: 'all' },
      disabled: disableButtons,
    },
    {
      label: 'Imagine with AI',
      value: 'imagine_with_ai',
      icon: 'imagesearch_roller',
      leadingIconType: 'component',
      leadingIconComponentProps: { external: true, externalType: 'all' },
      disabled: disableButtons,
    },
  ];
};

export const QUICK_WINS_SETTINGS = [
  {
    name: 'topWins',
    label: 'Top wins',
    options: ['3', '5', '10'],
    multiple: false
  },
  {
    name: 'periodOfTime',
    label: 'Period of time',
    options: ['This fiscal year', '1-2 budget cycles', '3+ years'],
    multiple: false
  },
  {
    name: 'impactTolerance',
    label: 'Staff impact tolerance',
    options: ['None', 'Minor restructuring', 'Position changes'],
    multiple: false
  },
  {
    name: 'complexity',
    label: 'Implementation complexity',
    options: ['Simple', 'Medium', 'Complex'],
    multiple: false
  },
  {
    name: 'absorption',
    label: 'Push back absorption',
    options: ['No brainer', 'Moderate', 'Controversial'],
    multiple: false
  },
  {
    name: 'reception',
    label: 'Public/council reception',
    options: ['Citizen positive', 'Neutral', 'Any'],
    multiple: false
  },
  {
    name: 'opportunity',
    label: 'What kind of opportunity',
    options: ['Cost reduction', 'Revenue enhancement', 'Service consolidation', 'Fee/cost recovery', 'Process efficiency'],
    multiple: true
  },
  {
    name: 'minimumSavingsTarget',
    label: 'Minimum savings target',
    options: ['Any amount', '$10k+', '$100k+', '$500k+'],
    multiple: false
  }
];

export const QUICK_WINS_INITIAL_DATA = {
  departmentName: '',
  topWins: QUICK_WINS_SETTINGS[0].options[0],
  periodOfTime: QUICK_WINS_SETTINGS[1].options[0],
  impactTolerance: QUICK_WINS_SETTINGS[2].options[0],
  complexity: QUICK_WINS_SETTINGS[3].options[0],
  absorption: QUICK_WINS_SETTINGS[4].options[0],
  reception: QUICK_WINS_SETTINGS[5].options[0],
  opportunity: [QUICK_WINS_SETTINGS[6].options[0]],
  minimumSavingsTarget: QUICK_WINS_SETTINGS[7].options[0],
};

export const shouldDisablePrimaryButton = (data) => {
  return !data.departmentName ||
    !data.topWins ||
    !data.periodOfTime ||
    !data.impactTolerance ||
    !data.complexity ||
    !data.absorption ||
    !data.reception ||
    !data.minimumSavingsTarget ||
    !data.opportunity?.length;
};

export const shouldDisablePrimaryButtonReimagine = (data) => {
  return !data.custom_prompt;
};

export const REIMAGINE_WITH_AI_INITIAL_DATA = {
  department: '',
  custom_prompt: '',
  reimagine_option: 'custom',
};

export const EXPANSIONS_OPTIONS = [
  {
    title: 'Effectiveness — Delivering better outcomes',
    options: [
      { text: 'Real-world examples of AI improving outcomes', value: 'real-world examples of ai improving outcomes' },
      { text: 'AI effectiveness plays across all 16 PBB categories', value: 'ai effectiveness plays across all 16 pbb categories' },
      { text: 'Top 5 effectiveness opportunities in my data', value: 'top 5 effectiveness opportunities in my data' }
    ]
  },
  {
    title: 'Efficiency & Optimization — Doing more with less',
    options: [
      { text: 'Real-world AI efficiency wins in government', value: 'real-world ai efficiency wins in government' },
      { text: 'Private-sector AI plays government hasn\'t adopted yet', value: 'private-sector ai plays government hasn\'t adopted yet' },
      { text: 'Top 10 AI efficiency candidates in MY portfolio', value: 'top 10 ai efficiency candidates in my portfolio' }
    ]
  },
  {
    title: 'Reimagine - What if we started from scratch?',
    options: [
      { text: 'Blank-sheet redesign of service delivery', value: 'blank-sheet redesign of service delivery' },
      { text: 'Agentic AI applications for government', value: 'agentic ai applications for government' },
      { text: 'Reimagined Portfolio vision for MY departments', value: 'reimagined portfolio vision for my departments' }
    ]
  },
];

export const STREAM_INACTIVITY_TIMEOUT_MS = Number(process.env.STREAM_INACTIVITY_TIMEOUT_MS) || 20000;
